/**
 * Safe markdown renderer for static legal copy (audit P-4/P-12, 2026-10-22).
 *
 * Renders a bounded subset of markdown (headings, paragraphs, lists, emphasis,
 * blockquotes, rules and links) as React elements. Raw HTML is NEVER rendered
 * (no dangerouslySetInnerHTML, skipHtml drops embedded HTML nodes), and links
 * are only clickable when they are absolute https: URLs — everything else shows
 * as plain text. The input is a trusted, counsel-reviewed static file, but it is
 * rendered defensively regardless.
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { parseSafeHref } from '../chat/SafeMarkdownLink';

// The only elements legal copy needs. Anything else is unwrapped to its text.
// Tables (GFM) are included for the privacy policy's sub-processor / retention
// tables; raw HTML is still never rendered (skipHtml).
const ALLOWED = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'ul', 'ol', 'li',
  'a', 'strong', 'em', 'blockquote', 'hr', 'br',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
];

function SafeLink({ href, children }) {
  const url = parseSafeHref(href);
  if (!url) return <span>{children}</span>;
  return (
    <a href={url.href} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">
      {children}
    </a>
  );
}

/**
 * @param {{ content: string }} props
 */
export default function SafeMarkdown({ content }) {
  return (
    <ReactMarkdown
      skipHtml
      allowedElements={ALLOWED}
      unwrapDisallowed
      remarkPlugins={[remarkGfm]}
      components={{ a: SafeLink }}
    >
      {typeof content === 'string' ? content : ''}
    </ReactMarkdown>
  );
}
